import React, { useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../../../authConfig";
import LocalNavbar from "../LoginCredentialsTemporaryPassword/LocalComponents/LocalNavbar";
import loadingGif from "../../../Assets/Images/Animations/loading-management-console.gif";
import invalidIllustration from "../../../Assets/Images/Illustrations/delete-card-img.svg";
import styles from "./loginRedirectionForgetPassword.module.scss";
import { userApi } from "../../../Utils/API";
import { VALIDATE_FORGET_PASSWORD_TOKEN } from "../../../Utils/GlobalConstants";
import MailIcon from "@mui/icons-material/Mail";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import HomeIcon from "@mui/icons-material/Home";
import LoginIcon from "@mui/icons-material/Login";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

// API: GET /user/api/user/validate-token?token={token}
// Response shapes:
//   { tokenIsValid: true,  redirectUri: "https://...", message: "Token is valid.", linkStatus: null,          supportEmail: null }
//   { tokenIsValid: false, redirectUri: null, message: "Token expired.",           linkStatus: "Expired",     supportEmail: "..." }
//   { tokenIsValid: false, redirectUri: null, message: "Token superseded...",      linkStatus: "Superseded",  supportEmail: "..." }
//   { tokenIsValid: false, redirectUri: null, message: "Token already redeemed.",  linkStatus: "AlreadyUsed", supportEmail: "..." }
//   HTTP 400 → invalid token  (supportEmail is NOT returned for this case)
//   other errors → treat as expired (safe default)
// supportEmail: only present on the 3 tokenIsValid:false error states.
//   If non-null and non-empty, replaces the fallback support address in the
//   "Contact Support" button and "online support" inline link.

const STEPS = [
  { key: "validating", label: "Validating Link" },
  { key: "redirecting", label: "Redirecting" },
];

const LoginRedirectionForgetPassword = () => {
  const { instance } = useMsal();

  // Raw token extraction — preserves + characters in Base64 tokens.
  const rawQuery = window.location.search;
  const tokenMatch = rawQuery.match(/[?&]token=([^&]*)/);
  const token = tokenMatch ? tokenMatch[1] : null;

  // "loading" | "redirecting" | "expired" | "superseded" | "alreadyUsed" | "invalid"
  const [pageState, setPageState] = useState("loading");

  // supportEmail from API — only populated on the 3 tokenIsValid:false error states.
  // null → fall back to the default Synnex support address.
  const FALLBACK_SUPPORT_EMAIL = "online-support@au.synnex-grp.com";
  const [supportEmail, setSupportEmail] = useState(null);

  // Returns the effective mailto href: API value when valid, otherwise fallback.
  const getSupportMailto = () =>
    supportEmail && supportEmail.trim()
      ? `mailto:${supportEmail.trim()}`
      : `mailto:${FALLBACK_SUPPORT_EMAIL}`;

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!token) {
      setPageState("expired");
      return;
    }

    userApi
      .get(`${VALIDATE_FORGET_PASSWORD_TOKEN}?token=${token}`)
      .then((resp) => {
        const data = resp.data;
        if (data && data.tokenIsValid === true && data.redirectUri) {
          setTimeout(() => {
            setPageState("redirecting");
            setTimeout(() => {
              window.location.href = data.redirectUri;
            }, 3000);
          }, 1000);
        } else if (data && data.tokenIsValid === false) {
          // Capture supportEmail — only present on these 3 tokenIsValid:false error states
          if (data.supportEmail && data.supportEmail.trim()) {
            setSupportEmail(data.supportEmail.trim());
          }
          const status = (data.linkStatus || "").toLowerCase();
          if (status === "superseded") {
            setPageState("superseded");
          } else if (status === "alreadyused") {
            setPageState("alreadyUsed");
          } else {
            setPageState("expired");
          }
        } else {
          setPageState("expired");
        }
      })
      .catch((err) => {
        const httpStatus = err.response?.status;
        if (httpStatus === 400) {
          setPageState("invalid");
        } else {
          setPageState("expired");
        }
      });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Triggers MSAL Azure B2C sign-in redirect
  const handleSignIn = () => {
    instance.loginRedirect(loginRequest).catch((error) => console.log(error));
  };

  // ── Loading / Redirecting — branded status card ───────────────────────────
  const renderStatusCard = () => {
    const activeStep = pageState === "redirecting" ? 1 : 0;
    const title = pageState === "redirecting" ? "Redirecting You" : "Validating Your Link";
    const subtitle =
      pageState === "redirecting"
        ? "Your link is verified. Taking you to the password reset page…"
        : "Please wait while we verify your password reset link.";

    return (
      <div className={styles["status-card"]}>
        <div className={styles["status-card-bar"]} />

        <div className={styles["spinner-wrap"]}>
          <img src={loadingGif} alt={title} className={styles["spinner-gif"]} />
        </div>

        <h2 className={styles["status-title"]}>{title}</h2>
        <p className={styles["status-subtitle"]}>{subtitle}</p>

        <div className={styles["steps"]}>
          {STEPS.map((step, idx) => (
            <div
              key={step.key}
              className={`${styles["step"]} ${
                idx < activeStep
                  ? styles["step--done"]
                  : idx === activeStep
                  ? styles["step--active"]
                  : styles["step--pending"]
              }`}
            >
              <div className={styles["step-dot"]}>
                {idx < activeStep && (
                  <svg viewBox="0 0 12 12" className={styles["step-check"]}>
                    <polyline points="2,6 5,9 10,3" />
                  </svg>
                )}
              </div>
              <span className={styles["step-label"]}>{step.label}</span>
            </div>
          ))}
        </div>

        {pageState === "redirecting" && (
          <div className={styles["progress-bar-track"]}>
            <div className={styles["progress-bar-fill"]} />
          </div>
        )}
      </div>
    );
  };

  // ── Shared "What can I do?" section ──────────────────────────────────────
  // line1Node — state-specific hint (MailIcon row).
  // primaryBtn — varies: contactSupportBtn for expired/superseded, signInBtn for alreadyUsed.
  // "online support" link uses getSupportMailto() — API supportEmail if set, else fallback.
  // Icons wrapped in <span> so SCSS targets svg path { fill } reliably.
  const renderWhatCanIDo = (line1Node, primaryBtn) => (
    <div className={styles["wcd-section"]}>
      <hr className={styles["wcd-divider"]} />
      <h3 className={styles["wcd-title"]}>What can I do?</h3>

      <div className={styles["wcd-item"]}>
        <span className={styles["wcd-icon--blue"]}>
          <MailIcon style={{ fontSize: "1.25rem" }} />
        </span>
        <p className={styles["wcd-item-text"]}>{line1Node}</p>
      </div>

      <div className={styles["wcd-item"]}>
        <span className={styles["wcd-icon--blue"]}>
          <SupportAgentIcon style={{ fontSize: "1.25rem" }} />
        </span>
        <p className={styles["wcd-item-text"]}>
          Contact{" "}
            <strong>online support</strong>
            {" "}
          if you continue to experience issues.
        </p>
      </div>

      <div className={styles["wcd-actions"]}>
        {primaryBtn}
        <a href="/" className={styles["wcd-btn-secondary"]}>
          <span className={styles["wcd-icon--blue"]}>
            <HomeIcon style={{ fontSize: "1.125rem" }} />
          </span>
          Go to Homepage
        </a>
      </div>
    </div>
  );

  // Default primary button — Contact Support
  // Uses API supportEmail if provided (expired/superseded/alreadyUsed states), otherwise falls back to default Synnex address.
  const contactSupportBtn = (
    <a
      href={getSupportMailto()}
      className={styles["wcd-btn-primary"]}
    >
      <span className={styles["wcd-icon--white"]}>
        <SupportAgentIcon style={{ fontSize: "1.125rem" }} />
      </span>
      Contact Support
    </a>
  );

  // AlreadyUsed primary button — triggers MSAL B2C loginRedirect
  const signInBtn = (
    <button
      type="button"
      onClick={handleSignIn}
      className={styles["wcd-btn-primary"]}
    >
      <span className={styles["wcd-icon--white"]}>
        <LoginIcon style={{ fontSize: "1.125rem" }} />
      </span>
      Go to Sign In
    </button>
  );

  // ── Expired State ─────────────────────────────────────────────────────────
  const renderExpired = () => (
    <div className={styles["state-card"]}>
      {/* Red circle — same 9rem visual size as other icon circles */}
      <div className={styles["icon-circle--red"]}>
        <AccessTimeIcon style={{ fontSize: "2.75rem" }} />
      </div>
      <h2 className={styles["state-title"]}>The link is expired</h2>
      <p className={styles["state-body"]}>
        This password reset link is no longer valid. Links expire after a set
        period or have already been used.
      </p>
      {renderWhatCanIDo(
        <>
          Request a <strong>new password reset link</strong> in your email.
        </>,
        contactSupportBtn
      )}
    </div>
  );

  // ── Superseded State ──────────────────────────────────────────────────────
  const renderSuperseded = () => (
    <div className={styles["state-card"]}>
      {/* Blue circle — same 9rem visual size as SVG illustrations */}
      <div className={styles["icon-circle--blue"]}>
        <MailIcon style={{ fontSize: "2.75rem" }} />
      </div>
      <h2 className={styles["state-title"]}>This password reset link is no longer active</h2>
      <p className={styles["state-body"]}>
        Please
        use the most recent email from Synnex to set your password.
      </p>
      {renderWhatCanIDo(
        <>
          Check your email inbox for the{" "}
          <strong>most recent password link</strong> from Synnex.
        </>,
        contactSupportBtn
      )}
    </div>
  );

  // ── Already Used State ────────────────────────────────────────────────────
  const renderAlreadyUsed = () => (
    <div className={styles["state-card"]}>
      {/* Green circle — same 9rem visual size as SVG illustrations */}
      <div className={styles["icon-circle--green"]}>
        <CheckCircleIcon style={{ fontSize: "2.75rem" }} />
      </div>
      <h2 className={styles["state-title"]}>This link has already been used</h2>
      <p className={styles["state-body"]}>
        If
        you've forgotten your password, please use the Sign In page to reset
        it.
      </p>
      {renderWhatCanIDo(
        <>
          Your account is already active —{" "}
            <strong>sign in now</strong>
          {" "}
          to continue.
        </>,
        signInBtn
      )}
    </div>
  );

  // ── Invalid Token State ───────────────────────────────────────────────────
  // supportEmail is NOT returned for HTTP 400 (invalid).
  // No "Contact Support" button or "online support" link shown.
  // Only the mail hint + a solid-blue "Go to Homepage" button (wcd-btn-primary).
  const renderInvalid = () => (
    <div className={styles["state-card"]}>
      <img
        src={invalidIllustration}
        alt="Invalid link"
        className={styles["state-illustration"]}
      />
      <h2 className={styles["state-title"]}>The link is invalid</h2>
      <p className={styles["state-body"]}>
        The password reset link you followed is not valid.
      </p>

      <div className={styles["wcd-section"]}>
        <hr className={styles["wcd-divider"]} />
        <h3 className={styles["wcd-title"]}>What can I do?</h3>

        <div className={styles["wcd-item"]}>
          <span className={styles["wcd-icon--blue"]}>
            <MailIcon style={{ fontSize: "1.25rem" }} />
          </span>
          <p className={styles["wcd-item-text"]}>
            Use the <strong>exact link</strong> from your password reset email.
          </p>
        </div>

        <div className={styles["wcd-actions"]}>
          <a href="/" className={styles["wcd-btn-primary"]}>
            <span className={styles["wcd-icon--white"]}>
              <HomeIcon style={{ fontSize: "1.125rem" }} />
            </span>
            Go to Homepage
          </a>
        </div>
      </div>
    </div>
  );

  // ── Main Render ───────────────────────────────────────────────────────────
  return (
    <div className={styles["page-wrapper"]}>
      <LocalNavbar />

      <div className={`${styles["page-content"]} ${styles["page-content--centered"]}`}>
        <Container>
          <Row className="justify-content-center">
            <Col xs={12} sm={10} md={8} lg={6} xl={5}>
              {(pageState === "loading" || pageState === "redirecting") && renderStatusCard()}
              {pageState === "expired" && renderExpired()}
              {pageState === "superseded" && renderSuperseded()}
              {pageState === "alreadyUsed" && renderAlreadyUsed()}
              {pageState === "invalid" && renderInvalid()}
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
};

export default LoginRedirectionForgetPassword;
